import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { listingApiIsomorphicFetchEvent } from '#/modules/listing/events/api/listingApiFetchEvent';
import { UnpublishedEventPageLoadable } from '#/modules/listing/events/pages/unpublished/UnpublishedEventPageLoadable';
import type { viewEventReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';

import { ViewEventPageLoadable } from './ViewEventPageLoadable';

export const handle = {
  defaultSearchType: 'EVENT',
  showSearchRefinements: true,
  showDesktopSearch: true,
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
  params: { eventIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewEventReferenceRoute>) {
  const [eventId] = eventIdDashSlug.split('-');

  try {
    const [event] = await Promise.all([
      listingApiIsomorphicFetchEvent({ request, context })(eventId),
      // It's possible this loadable won't be used if the event is unpublished
      // but in most cases optimistically loading it is best
      ViewEventPageLoadable.load(),
    ]);

    const fetchedListingExtras =
      await userDashboardApiIsomorphicFetchListingExtras({
        request,
        context,
      })([eventId, event.org?.id].filter(Boolean));

    const saved = Boolean(fetchedListingExtras[eventId]?.saved);

    const orgSaved = Boolean(
      event.org && fetchedListingExtras[event.org.id]?.saved,
    );

    switch (event.type) {
      case 'EVENT':
        return {
          type: event.type,
          event,
          handle: {
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: event.url,
          } satisfies LayoutHandleProps,
          saved,
          orgSaved,
        } as const;
      case 'UNPUBLISHED_EVENT':
        await UnpublishedEventPageLoadable.load();
        return {
          type: event.type,
          unpublishedEvent: event,
        } as const;
    }
  } catch {
    return {
      type: 'NOT_FOUND',
    } as const;
  }
}

export default function ViewEventHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  switch (loaderData.type) {
    case 'NOT_FOUND':
      return <NotFoundPageLoadable noLayout />;
    case 'EVENT':
      return (
        <RouteModule
          title={`${loaderData.event.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['EVENT']}`}
        >
          <ViewEventPageLoadable
            event={loaderData.event}
            initiallySaved={loaderData.saved}
            orgInitiallySaved={loaderData.orgSaved}
          />
        </RouteModule>
      );

    case 'UNPUBLISHED_EVENT':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <UnpublishedEventPageLoadable
            unpublishedEvent={loaderData.unpublishedEvent}
          />
        </RouteModule>
      );
  }
}
